import React from 'react';
import HeroSection from '../components/HeroSection';
import Picture from '../components/Picture';
import SEO from '../components/SEO';

const HERO_IMG = '/images/bonfire-hero-2026.jpg';
const MAP_IMG = '/images/bonfire-event-map-v2.png';
const WHATS_ON_IMG = '/images/bonfire-whats-on-2026.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICKET_URL = 'https://www.ticketsource.com/glossop-roundtable/glossop-bonfire-spectacular-7-november-2026/e-arjqor';
// Backend QR generator (public endpoint, long-cached) — site_url is the direct
// bonfire-only ticket URL so scanners don't land on the mixed Round Table /
// Heritage Weekend landing page.
const TICKET_QR_URL = `${API}/qr?url=${encodeURIComponent(TICKET_URL)}&size=600`;

const TIMETABLE = [
  { time: '4:00pm',  item: 'Gates open' },
  { time: '5:00pm — 5:45pm', item: 'Tintwistle Band' },
  { time: '5:00pm — 5:45pm', item: 'Centre Stage and Fire Breathers' },
  { time: '5:45pm',  item: 'Kids Fireworks Display' },
  { time: '6:00pm',  item: 'Bonfire lit' },
  { time: '6:15pm — 6:45pm', item: 'Tintwistle Band' },
  { time: '6:15pm — 7:00pm', item: 'Centre Stage and Fire Breathers' },
  { time: '7:15pm',  item: 'Main Fireworks Display' },
];

export default function BonfirePage() {
  return (
    <div data-testid="bonfire-page">
      <SEO
        title="Glossop Bonfire & Fireworks Spectacular"
        description="Glossop Bonfire & Fireworks Spectacular returns in November 2026 at Manor Park. Join the launch list to be first to hear when tickets go on sale. All profits support local charities."
        path="/bonfire"
        keywords={['Glossop Bonfire Night', 'Glossop fireworks', 'Glossop bonfire 2026', 'Manor Park Glossop', 'fireworks display Glossop', 'bonfire night Derbyshire', 'Glossop fireworks tickets']}
      />
      <HeroSection
        image={HERO_IMG}
        title="Glossop Bonfire & Fireworks Spectacular!"
        subtitle="Returning November 2026"
        fullHeight={false}
      />

      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-4">
              Saturday 7 November 2026 · Manor Park
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] mb-4">
              Watch in awe as the night sky lights up with a thrilling display of fireworks!
            </h2>
            <p className="text-base text-[#52525B] leading-relaxed max-w-2xl mx-auto">
              Every ticket you buy puts money straight back into Glossop charities and community projects. Thanks for supporting the event.
            </p>
          </div>

          {/* TICKETS ON SALE — direct TicketSource bonfire URL + printable QR for shop windows / flyers */}
          <div className="mb-12 bg-[#0A0A0A] text-white p-6 sm:p-10 border-l-4 border-[#F0A500]" data-testid="bonfire-tickets-card">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#F0A500] block mb-3">Tickets on sale now</span>
                <h3 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Grab your tickets online</h3>
                <p className="text-base text-zinc-300 leading-relaxed mb-6">
                  Advance tickets are cheaper than on the gate and guarantee entry on the night. Secure checkout through TicketSource &mdash; e-tickets emailed instantly.
                </p>
                <a
                  href={TICKET_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bonfire-buy-tickets-btn"
                  className="inline-block px-8 py-3.5 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-sm transition-colors"
                >
                  Buy tickets &rarr;
                </a>
                <p className="text-xs text-zinc-400 mt-4">Hosted on TicketSource &middot; direct link above the QR code is also safe to share.</p>
              </div>
              <div className="flex flex-col items-center justify-center">
                <img
                  src={TICKET_QR_URL}
                  alt="QR code — scan to buy Glossop Bonfire Night tickets on TicketSource"
                  className="w-40 h-40 sm:w-44 sm:h-44 bg-white p-2"
                  data-testid="bonfire-tickets-qr"
                  loading="lazy"
                />
                <span className="text-[11px] uppercase tracking-widest text-zinc-400 mt-3">Scan to buy</span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Advance Pricing */}
            <div className="p-6 bg-[#F4F4F5] border-l-4 border-[#F0A500]">
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-3">Advance Ticket Pricing</h3>
              <ul className="space-y-2 text-sm text-[#52525B]">
                <li><strong>Family:</strong> &pound;25.00 (two adults and two concessions)</li>
                <li><strong>Adult (16-65):</strong> &pound;10.00</li>
                <li><strong>Concession (3-15, Students, DLA/PIP, 66+):</strong> &pound;5.00</li>
                <li><strong>Under 3s:</strong> Free</li>
              </ul>
            </div>

            {/* On the Gate Pricing */}
            <div className="p-6 bg-[#F4F4F5] border-l-4 border-[#0A0A0A]">
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-3">On the Gate Pricing</h3>
              <ul className="space-y-2 text-sm text-[#52525B] mb-4">
                <li><strong>Family:</strong> &pound;33.00 (two adults and two concessions)</li>
                <li><strong>Adult (16-65):</strong> &pound;13.00</li>
                <li><strong>Concession (3-15, Students, DLA/PIP, 66+):</strong> &pound;7.00</li>
                <li><strong>Under 3s:</strong> Free</li>
              </ul>
              <p className="text-xs uppercase tracking-widest font-bold text-[#D62F2F]" data-testid="bonfire-cashless-note">
                ⚠ This year we&rsquo;re going cashless &mdash; card payments only on the gate.
              </p>
            </div>

            {/* Timetable */}
            <div className="p-6 bg-[#F4F4F5] border-l-4 border-[#F0A500]" data-testid="bonfire-timetable">
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-4">Event Timetable</h3>
              <dl className="divide-y divide-zinc-300">
                {TIMETABLE.map((row, i) => (
                  <div key={i} className="grid grid-cols-[auto_1fr] gap-x-5 py-2 text-sm">
                    <dt className="font-bold text-[#0A0A0A] whitespace-nowrap">{row.time}</dt>
                    <dd className="text-[#52525B]">{row.item}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-xs text-[#71717A] mt-4 italic">(All timings and acts subject to change.)</p>
            </div>

            {/* Safety warnings — pets, sparklers. Red border + bold heading
                because these are the two things people most commonly try to
                bring onto the park. */}
            <div className="p-6 bg-[#FEF2F2] border-l-4 border-[#D62F2F]" data-testid="bonfire-safety-warnings">
              <h3 className="text-lg font-bold text-[#D62F2F] mb-3 uppercase tracking-wider">⚠ Please note</h3>
              <ul className="space-y-2 text-sm text-[#0A0A0A]">
                <li data-testid="bonfire-no-pets">
                  <strong>No pets allowed</strong> in the park during the evening&rsquo;s event. The fireworks, bonfire and large crowds are distressing for animals &mdash; please leave pets safely at home.
                </li>
                <li data-testid="bonfire-no-sparklers">
                  <strong>No sparklers</strong> may be brought in or used at the event. Please don&rsquo;t bring them along &mdash; stewards will ask you to dispose of them at the gate.
                </li>
              </ul>
            </div>

            {/* Helpful websites (mirrored from the News page top story) — for
                pet owners, families with kids who are scared of fireworks, and
                anyone with PTSD. */}
            <div className="p-6 bg-[#F4F4F5] border-l-4 border-[#0A0A0A]" data-testid="bonfire-helpful-links">
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-3">Helpful websites</h3>
              <p className="text-sm text-[#52525B] mb-4 leading-relaxed">
                We know fireworks can be distressing for pets, young children and people living with PTSD. These guides offer practical ways to prepare and cope.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: 'RSPCA', url: 'https://www.rspca.org.uk/adviceandwelfare/pets/general/fireworks' },
                  { label: 'Dogs Trust', url: 'https://www.dogstrust.org.uk/dog-advice/life-with-your-dog/seasonal/fireworks' },
                  { label: "Cat's Protection", url: 'https://www.cats.org.uk/help-and-advice/home-and-environment/fireworks' },
                  { label: 'BBC – Fireworks for Children', url: 'https://www.bbc.co.uk/tiny-happy-people/bonfire-night-kids-scared-fireworks/z7pyydm' },
                  { label: 'Help with PTSD', url: 'https://patient.info/news-and-features/managing-ptsd-during-fireworks-season' },
                ].map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-[#F0A500] hover:text-[#D99500] transition-colors"
                  >
                    {link.label} &rarr;
                  </a>
                ))}
              </div>
            </div>

            <div className="text-base text-[#52525B] leading-relaxed space-y-4">
              <p>
                Glossop Round Table have held the Glossop Bonfire and Fireworks Display in Manor Park for over 50 years. It's our largest event of the year and allows us to raise thousands of pounds in donations which we give out to local charities and good causes.
              </p>
              <p>
                All profits go to local good causes and charities such as Glossopdale Foodbank, Cafe Jericho, Hadfield Coming Together Group, Sandwich Angels and many more.
              </p>
            </div>

            <div className="space-y-4" data-testid="bonfire-event-map">
              <h3 className="text-xl font-bold text-[#0A0A0A]">Event Map</h3>
              <a href={MAP_IMG} target="_blank" rel="noopener noreferrer" data-testid="bonfire-event-map-link" className="block">
                <Picture src={MAP_IMG} alt="Glossop Bonfire 2026 event map — Manor Park entrances, stage, food court, first aid and fireworks zone" className="w-full h-auto border border-zinc-200" />
              </a>
            </div>

            <div className="space-y-4" data-testid="bonfire-whats-on">
              <h3 className="text-xl font-bold text-[#0A0A0A]">What's On</h3>
              <a href={WHATS_ON_IMG} target="_blank" rel="noopener noreferrer" data-testid="bonfire-whats-on-link" className="block">
                <Picture src={WHATS_ON_IMG} alt="Glossop Bonfire 2026 What's On schedule — Saturday 7th November, Manor Park" className="w-full h-auto border border-zinc-200" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
